import type { CommandEntryInputDto, Schedule, ScheduleInputDto } from '@pams/shared';
import { commandKeyOf, generateSlots, showsPerSlotMapper } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ADAPTIVE MODEL, IN ONE SCREENFUL.
 *
 *  ⚠️ EVERY CONDITIONAL CONTROL ON THE INTAKE IS DECIDED HERE AND NOWHERE ELSE.
 *
 *  The client describes four kinds of arrangement. Three of them are ONE form at three
 *  depths, and the depth follows from what the operator has entered rather than from
 *  anything they picked — there is no type picker and no mode selection anywhere on the
 *  screen. A reviewer must be able to read the whole adaptive model in one place; the
 *  alternative is hunting `&&`s across six components and discovering, a phase later, that
 *  two of them disagree about what "multiple shifts" means.
 *
 *  ⚠️ THE FAILURE THIS PREVENTS IS SPECIFIC. Exit criterion 2 says a single-place,
 *  single-slot, counts-only arrangement renders NO slot strip, NO per-shift mapper, NO
 *  same-for-all control and NO concurrent/sequential choice. Every one of those is a
 *  predicate below, and `arrangementShape.test.ts` asserts all four are false for the
 *  simplest arrangement — so the model cannot leak without a test going red.
 *
 *  ⚠️ AND IT SHRINKS. Criterion 3 is "removing it hides it again CLEANLY". Every predicate
 *  is a pure function of the CURRENT form values, so nothing latches: delete the second
 *  shift and the strip is gone on the same render. What happens to the DATA the hidden
 *  control was holding is a separate decision, taken in `pruneHiddenState` below.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type ShapeInput = {
  schedule: ScheduleInputDto | undefined;
  commandEntries: readonly CommandEntryInputDto[];
};

export type ArrangementDisclosure = {
  /** How many duty slots the schedule currently implies. 0 while it is unusable. */
  slotCount: number;
  /** The keys those slots carry, which the per-shift mapper and the overrides address. */
  slotKeys: string[];
  /** ⚠️ 2+ places. Below that the mode is `SINGLE` and no control is ever shown. */
  /**
   * ⚠️ [amended D4] REMOVED. Two places at once is TWO DUTY POINTS and a route is one card
   * with two stops, so there is no mode to reveal — the question is answered by where the
   * operator put the place. Exit criterion 2 is met more strongly than by hiding a control:
   * it is unrepresentable.
   */
  /** ⚠️ 2+ slots. One chip per generated slot, with a live count. */
  showsSlotStrip: boolean;
  /** ⚠️ 2+ slots. "Same for all shifts", and the per-slot "different here" actions. */
  showsSameForAllShifts: boolean;
  /**
   * ⚠️ Per ROLE, on BOTH conditions. See `showsPerSlotMapper`.
   * ⚠️ [amended D4.3] These are `commandKeyOf` VALUES (`R:…` / `A:…`), not raw policy keys —
   * a one-off row has no policy key and would otherwise be unaddressable here.
   */
  perSlotMapperRoleKeys: string[];
  /** Whether ANY role gets a mapper — what criterion 4 asserts. */
  showsAnyPerSlotMapper: boolean;
};

/**
 * The slots a schedule implies, or none.
 *
 * ⚠️ `generateSlots` IS THE ONLY DERIVATION, ON BOTH SIDES OF THE WIRE. A half-typed
 * schedule throws (an empty date, an end before a start), and a form mid-keystroke is the
 * normal case rather than an error — so it is caught and read as "nothing yet". Anything
 * that counted slots a second way here would disagree with the publish that creates them.
 */
const slotsOf = (schedule: ScheduleInputDto | undefined): { slotCount: number; slotKeys: string[] } => {
  if (!schedule) return { slotCount: 0, slotKeys: [] };
  try {
    const slots = generateSlots(schedule as Schedule);
    return { slotCount: slots.length, slotKeys: slots.map((slot) => slot.slotKey) };
  } catch {
    return { slotCount: 0, slotKeys: [] };
  }
};

export const disclosure = (input: ShapeInput): ArrangementDisclosure => {
  const { slotCount, slotKeys } = slotsOf(input.schedule);
  const multiSlot = slotKeys[1] !== undefined;

  /*
   * ⚠️ PER ROLE, NOT PER PAGE, AND THE DIFFERENCE IS EXIT CRITERION 4.
   *
   * A page-level "any role has two officers" would render a mapper beside a role that has
   * one — the same control the whole adaptive model exists to keep off a simple screen.
   * `showsPerSlotMapper` is the shared function, so the screen and any later reader ask
   * the identical question.
   */
  const perSlotMapperRoleKeys = input.commandEntries
    .filter((entry) => showsPerSlotMapper(entry, slotKeys))
    .map((entry) => commandKeyOf(entry));

  return {
    slotCount,
    slotKeys,
    showsSlotStrip: multiSlot,
    showsSameForAllShifts: multiSlot,
    perSlotMapperRoleKeys,
    showsAnyPerSlotMapper: perSlotMapperRoleKeys[0] !== undefined,
  };
};

/**
 * ⚠️ RE-EXPORTED, NOT REDEFINED. The rule moved to `packages/shared` at P3b.1, because the
 * API prunes the same rows on the same condition and two implementations of "which rows
 * still address a live slot" is exactly the drift this file exists to prevent. It is
 * re-exported from here so that this module stays the one place a reader looks for the
 * page's shape rules — and so no import had to change.
 */
export { pruneHiddenState } from '@pams/shared';

/**
 * The line the right rail always shows: "1 arrangement · 3 shifts · 2 places · 12 personnel
 * · 2 divisions".
 *
 * ⚠️ `personnel` IS THE SUM OF THE REQUIREMENT TOTALS AND NOTHING ELSE.
 *
 * It cannot include a command officer, and that is held STRUCTURALLY rather than by an
 * `if`: this function takes `requirementTotals` and has no parameter through which a
 * command entry could arrive. FR-5.2.6b closes OD-9 precisely because everybody assumes
 * command counts toward force, and "nobody should ever be surprised by what they just
 * made" is exactly the line where that assumption would do damage.
 */
export type WillCreateInput = {
  deploymentCount: number;
  /**
   * ⚠️ [amended D4] REPLACED `slotCount`, AND THE OLD ONE WAS BECOMING A LIE.
   *
   * Summing "shifts" across cards with unrelated schedules produces a number an operator
   * would read and act on and which means nothing — a card running Mondays and a card
   * running three shifts a day do not add up to anything. How many CARDS the order has is a
   * true statement, and it is the one that explains the duty count beside it.
   */
  dutyPointCount: number;
  placeCount: number;
  requirementTotals: readonly number[];
  divisionCount: number;
};

/**
 * ⚠️ [amended D4.1] ONE ARITHMETIC, TWO LENGTHS — AND THE INPUT TYPE IS THE GUARANTEE.
 *
 * The full sentence is the rail's; the first three terms are the publish bar's, because the
 * bar has one line beside "Not saved" and five terms in it is a paragraph. Both come from
 * this list, so the two can never disagree about how many duties an operator is about to
 * create — which is the whole reason the number is shown twice.
 *
 * FR-5.2.6b survives the split intact: BOTH readers take `WillCreateInput`, which has no
 * parameter through which a command entry could arrive. The structural guarantee is on the
 * TYPE now rather than on one function's parameter list, so it covers whatever reads it next.
 */
const willCreateTerms = (input: WillCreateInput): readonly string[] => {
  const plural = (count: number, one: string, many = `${one}s`): string =>
    `${String(count)} ${count === 1 ? one : many}`;

  let personnel = 0;
  for (const total of input.requirementTotals) personnel += total;

  return [
    plural(input.deploymentCount, 'duty', 'duties'),
    plural(input.dutyPointCount, 'duty point'),
    plural(input.placeCount, 'place'),
    plural(personnel, 'person', 'personnel'),
    plural(input.divisionCount, 'division'),
  ];
};

export const willCreate = (input: WillCreateInput): string => willCreateTerms(input).join(' · ');

/**
 * The publish bar's version: duties, duty points, personnel.
 *
 * ⚠️ PLACES AND DIVISIONS ARE DROPPED, NOT ABBREVIATED. What the bar exists to stop is an
 * operator being surprised by what they published, and the surprise is always a COUNT of
 * work — how many duties, spread over how many cards, needing how many people. How many
 * places is already on every card's own summary line, and how many divisions is what the
 * confirmation dialog names one by one a moment later.
 */
export const willCreateSummary = (input: WillCreateInput): string => {
  const terms = willCreateTerms(input);
  return [terms[0], terms[1], terms[3]].join(' · ');
};
